import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface EditableEvaluationSummaryProps {
  summary: string | undefined;
  onSave: (summary: string | undefined) => void;
}

const MAX_SUMMARY_LENGTH = 500;

/**
 * Editable evaluation summary with inline editing
 */
export function EditableEvaluationSummary({ summary, onSave }: EditableEvaluationSummaryProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(summary || '');
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    const trimmedValue = value.trim();

    // Validation
    if (trimmedValue.length > MAX_SUMMARY_LENGTH) {
      setError(t('dashboard:editSummary.errorTooLong', { max: MAX_SUMMARY_LENGTH }));
      return;
    }

    // Save undefined if empty, otherwise save the trimmed value
    onSave(trimmedValue || undefined);
    setIsEditing(false);
    setError(null);
  };

  const handleCancel = () => {
    setValue(summary || '');
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <div className="space-y-2 group">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">{t('dashboard:details.summary')}</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
            aria-label={t('dashboard:editSummary.editButton')}
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
          </Button>
        </div>
        {summary ? (
          <p className="text-base leading-relaxed">{summary}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            {t('dashboard:editSummary.noSummary')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('dashboard:details.summary')}</h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSave}
            className="h-6 w-6 p-0 text-green-600 hover:text-green-700"
            aria-label={t('common:actions.save')}
          >
            <Check className="h-3 w-3" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-6 w-6 p-0 text-destructive hover:text-destructive/90"
            aria-label={t('common:actions.cancel')}
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <Textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={handleKeyDown}
        placeholder={t('dashboard:editSummary.placeholder')}
        className="min-h-[100px] resize-y"
        maxLength={MAX_SUMMARY_LENGTH}
        autoFocus
        aria-label={t('dashboard:editSummary.placeholder')}
        aria-invalid={!!error}
        aria-describedby={error ? 'summary-error' : 'summary-help'}
      />
      {error && (
        <p id="summary-error" role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <p id="summary-help">{t('dashboard:editSummary.hint')}</p>
        <p>
          {value.length}/{MAX_SUMMARY_LENGTH} {t('dashboard:editSummary.characters')}
        </p>
      </div>
    </div>
  );
}
